import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";

interface LoginProps {
  onLoginSuccess: (user: { username: string; isAdmin: boolean }) => void;
}

export function Login({ onLoginSuccess }: LoginProps) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "Prijava neuspješna");
      }

      const data = await res.json();
      onLoginSuccess({ username: data.username, isAdmin: data.isAdmin });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Prijava neuspješna");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="h-screen flex items-center justify-center animate-gradient-shift"
      style={{
        background: "var(--effect-login-gradient)",
        backgroundSize: "300% 300%",
      }}
    >
      <div
        className="w-full max-w-sm rounded-2xl border border-border p-8 backdrop-blur-2xl"
        style={{
          backgroundColor: "var(--effect-glass-surface)",
          boxShadow: "var(--effect-glow-lg)",
        }}
      >
        <div className="text-center mb-6">
          <h1
            className="font-display text-3xl font-bold tracking-tight mb-1"
            style={{ textShadow: "var(--effect-text-glow)" }}
          >
            Lunatik Studio
          </h1>
          <p className="text-sm text-muted-foreground">AI Kreativni Studio</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="username" className="text-sm text-muted-foreground">Korisničko ime</Label>
            <Input
              id="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Unesite korisničko ime"
              autoFocus
              required
              className="bg-secondary/30 border-border focus:border-primary/50 focus:ring-primary/20"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password" className="text-sm text-muted-foreground">Lozinka</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Unesite lozinku"
              required
              className="bg-secondary/30 border-border focus:border-primary/50 focus:ring-primary/20"
            />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <Button
            type="submit"
            className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
            style={{ boxShadow: "var(--effect-glow-md)" }}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Prijavljivanje...
              </>
            ) : (
              "Prijava"
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}
